/**
 * PROLAST — site-wide behaviour. No framework, no dependencies.
 *
 *   drawers        cart / search / menu share one modal controller (the
 *                  rest of the page goes inert while one is open)
 *   header         tone over the homepage billboard, hides on scroll-down,
 *                  returns on scroll-up; mega panels as disclosures (hover
 *                  with intent, tap to open, keyboard via toggle buttons)
 *   cart           AJAX add / change / discount, re-rendered through the
 *                  Section Rendering API so Liquid stays the only template
 *   search         predictive results from sections/predictive-search
 *   cards          second image fetched on first hover, not on page load
 *
 * One rAF-throttled scroll loop drives everything scroll-related —
 * IntersectionObserver callbacks stall in throttled renderers.
 */
(() => {
  const PL = (window.PL = window.PL || {});
  const routes = PL.routes || {};
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const root = document.documentElement;

  /* ------------------------------------------------------------------ *
   * Scroll loop
   * ------------------------------------------------------------------ */
  const scrollers = [];
  let ticking = false;
  function runScrollers() {
    ticking = false;
    const y = window.scrollY;
    for (const fn of scrollers) fn(y);
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(runScrollers);
  }
  addEventListener('scroll', onScroll, {passive: true});
  addEventListener('resize', onScroll, {passive: true});
  PL.onScroll = (fn) => {
    scrollers.push(fn);
    fn(window.scrollY);
  };

  /* ------------------------------------------------------------------ *
   * Drawers
   * ------------------------------------------------------------------ */
  let lastFocus = null;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';
  const inerted = [];

  function dialogOf(drawer) {
    return $('[role="dialog"]', drawer) || $('aside', drawer);
  }

  /* Everything outside the open drawer goes inert (not focusable, hidden
     from screen readers); only what we changed is restored. */
  function setOutsideInert(el) {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
      const parent = node.parentElement;
      if (!parent) break;
      for (const sibling of parent.children) {
        if (sibling === node || sibling.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sibling.tagName)) continue;
        sibling.inert = true;
        inerted.push(sibling);
      }
    }
  }

  function clearInert() {
    while (inerted.length) inerted.pop().inert = false;
  }

  function setExpanded(drawer, value) {
    for (const control of $$(`[aria-controls="${drawer.id}"]`)) {
      control.setAttribute('aria-expanded', String(value));
    }
  }

  function openDrawer(name) {
    const drawer = $(`[data-pl-drawer="${name}"]`);
    if (!drawer) return false;
    if (drawer.classList.contains('is-open')) return true;
    closeDrawers(false);
    closeMegaNow();
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    setExpanded(drawer, true);
    root.classList.add('pl-locked');
    setOutsideInert(drawer);
    // focus in the same task as the tap, so iOS opens the keyboard
    const target = $('[data-pl-psearch-input]', drawer) || dialogOf(drawer);
    if (target) target.focus({preventScroll: true});
    return true;
  }

  function closeDrawers(restoreFocus = true) {
    const open = $$('[data-pl-drawer].is-open');
    if (!open.length) return;
    for (const drawer of open) {
      drawer.classList.remove('is-open');
      setExpanded(drawer, false);
    }
    clearInert();
    root.classList.remove('pl-locked');
    if (restoreFocus && lastFocus && lastFocus.focus) lastFocus.focus({preventScroll: true});
  }

  PL.openDrawer = openDrawer;
  PL.closeDrawers = closeDrawers;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-pl-open]');
    if (opener) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (openDrawer(opener.dataset.plOpen)) event.preventDefault();
      return;
    }
    if (event.target.closest('[data-pl-close]')) {
      event.preventDefault();
      closeDrawers();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if ($('[data-pl-drawer].is-open')) {
        closeDrawers();
      } else if (header.open) {
        const toggle = $('.pl-navtoggle', header.open);
        const inside = header.open.contains(document.activeElement);
        closeMegaNow();
        if (inside && toggle) toggle.focus();
      }
      return;
    }
    if (event.key !== 'Tab') return;
    const drawer = $('[data-pl-drawer].is-open');
    const dialog = drawer && dialogOf(drawer);
    if (!dialog) return;
    const items = $$(FOCUSABLE, dialog).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------ *
   * Header — tone over the billboard, hide on scroll, mega panels
   *
   * Everything is delegated to document and the shell is looked up when
   * needed, so the header keeps working after the theme editor
   * re-renders the section.
   * ------------------------------------------------------------------ */
  const header = {shell: null, open: null, timer: 0, hidden: false, pointer: 'mouse', openedAt: 0};

  function shellEl() {
    if (!header.shell || !header.shell.isConnected) header.shell = $('[data-pl-header]');
    return header.shell;
  }

  /* Over the homepage billboard: clear at the very top, near-black once
     scrolled while still over the photo, white past it or with a panel
     open. Other pages are always white (tone set in Liquid). */
  function updateTone() {
    const shell = shellEl();
    if (!shell || !shell.hasAttribute('data-overlay')) return;
    let tone = 'light';
    const hero = $('.pl-bb');
    const row = $('.pl-header', shell);
    if (!header.open && hero && row && hero.getBoundingClientRect().bottom > row.getBoundingClientRect().bottom + 1) {
      tone = window.scrollY < 8 ? 'clear' : 'dark';
    }
    if (shell.dataset.tone !== tone) shell.dataset.tone = tone;
  }

  function openMega(item) {
    clearTimeout(header.timer);
    if (!item || header.open === item) return;
    const shellNow = shellEl();
    if (shellNow) warmCards(shellNow);
    closeMegaNow(false);
    const panel = $('.pl-mega', item);
    if (!panel) return;
    panel.hidden = false;
    item.classList.add('is-open');
    const toggle = $('.pl-navtoggle', item);
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    header.open = item;
    header.openedAt = window.scrollY;
    const shell = shellEl();
    if (shell) shell.classList.add('is-mega');
    updateTone();
  }

  function closeMegaNow(retone = true) {
    clearTimeout(header.timer);
    const item = header.open;
    if (!item) return;
    const panel = $('.pl-mega', item);
    if (panel) panel.hidden = true;
    item.classList.remove('is-open');
    const toggle = $('.pl-navtoggle', item);
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    header.open = null;
    const shell = shellEl();
    if (shell) shell.classList.remove('is-mega');
    if (retone) updateTone();
  }

  /* One timer: moving onto a label opens after a short intent delay, a
     longer one when switching panels (so a diagonal run to a photo card
     across another label doesn't flip the panel), and leaving closes. */
  function schedule(item, delay) {
    clearTimeout(header.timer);
    header.timer = setTimeout(() => (item ? openMega(item) : closeMegaNow()), delay);
  }

  document.addEventListener('pointerdown', (event) => {
    header.pointer = event.pointerType || 'mouse';
  }, {capture: true, passive: true});
  document.addEventListener('keydown', () => {
    header.pointer = 'keyboard';
  }, {capture: true});

  /* The panels' photo cards start loading the first time the pointer
     reaches the header, so a panel never opens onto empty frames. */
  function warmCards(shell) {
    if (shell.dataset.warm) return;
    shell.dataset.warm = '1';
    for (const img of $$('.pl-mcard img[loading="lazy"]', shell)) img.loading = 'eager';
  }

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const shell = shellEl();
    if (!shell || !shell.contains(event.target)) return;
    warmCards(shell);
    const item = event.target.closest('[data-mega-item]');
    if (item) {
      if (header.open === item) clearTimeout(header.timer);
      else schedule(item, header.open ? 160 : 90);
    } else if (header.open) {
      schedule(null, event.target.closest('.pl-navitem') ? 160 : 220);
    } else {
      clearTimeout(header.timer);
    }
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return;
    const shell = shellEl();
    if (!shell || !shell.contains(event.target) || shell.contains(event.relatedTarget)) return;
    if (header.open) schedule(null, 180);
    else clearTimeout(header.timer);
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.pl-navtoggle');
    if (toggle) {
      const item = toggle.closest('[data-mega-item]');
      if (header.open === item) closeMegaNow();
      else openMega(item);
      return;
    }
    const link = event.target.closest('.pl-navlink');
    if (link) {
      // touch and pen: the first tap on a label opens its panel, the
      // second follows the link
      const item = link.closest('[data-mega-item]');
      if (item && (header.pointer === 'touch' || header.pointer === 'pen') && header.open !== item) {
        event.preventDefault();
        openMega(item);
      }
      return;
    }
    if (header.open && !event.target.closest('[data-mega-item]')) closeMegaNow();
  });

  document.addEventListener('focusout', (event) => {
    if (header.open && event.relatedTarget && !header.open.contains(event.relatedTarget)) closeMegaNow();
  });

  function setHidden(value) {
    const shell = shellEl();
    if (!shell || header.hidden === value) return;
    header.hidden = value;
    shell.classList.toggle('is-hidden', value);
    root.classList.toggle('pl-hdr-hidden', value);
  }

  /* Hide on the way down; come back after a real scroll up (32px), near
     the top, or when anything in the header takes focus. The rubber-band
     at either end of the page is ignored. A page that pins its own content
     under the header ([data-pl-hdr-stay]: the custom-bag builder's 3D view)
     keeps it shown, so the pinned content never jumps with it. */
  const stays = () => !!document.querySelector('[data-pl-hdr-stay]');
  let lastY = window.scrollY;
  let upRun = 0;
  PL.onScroll((y) => {
    const shell = shellEl();
    if (!shell) return;
    updateTone();
    if (header.open && Math.abs(y - header.openedAt) > 80) closeMegaNow();
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const delta = y - lastY;
    lastY = y;
    if (y <= 0) {
      setHidden(false);
      return;
    }
    if (y >= max - 1 || !delta) return;
    if (delta > 0) {
      upRun = 0;
      const busy = header.open || root.classList.contains('pl-locked') || shell.querySelector(':focus-visible');
      if (y > 160 && !busy && !stays()) setHidden(true);
    } else {
      upRun -= delta;
      if (upRun > 32 || y < 160) setHidden(false);
    }
  });

  document.addEventListener('focusin', (event) => {
    const shell = shellEl();
    if (shell && shell.contains(event.target)) setHidden(false);
  });

  /* theme editor */
  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target.querySelector('[data-pl-header]')) return;
    header.shell = null;
    header.open = null;
    header.hidden = false;
    root.classList.remove('pl-hdr-hidden');
    updateTone();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest && event.target.closest('[data-mega-item]');
    if (item) openMega(item);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    if (header.open && event.target.contains(header.open)) closeMegaNow();
  });

  /* ------------------------------------------------------------------ *
   * Cart
   * ------------------------------------------------------------------ */
  function cartSectionIds() {
    const ids = $$('[data-cart-root]').map((el) => el.dataset.cartSection).filter(Boolean);
    return Array.from(new Set(ids));
  }

  /* The control that had focus, as a selector that finds its twin in the re-rendered cart: a stepper by its line's
     variant and its label, anything else by id (the discount field). By variant, not by the whole line key: Shopify
     re-keys a discounted line when its quantity changes (2026-10-10, the stock bag's automatic 10% off). A removed
     line has no twin. */
  function cartFocusKey(el) {
    if (el.matches('[data-cart-change][data-key]')) {
      const label = el.getAttribute('aria-label');
      if (!label) return null;
      const key = el.dataset.key;
      const cut = key.indexOf(':');
      const line = cut > 0 ? `[data-key^="${CSS.escape(key.slice(0, cut + 1))}"]` : `[data-key="${CSS.escape(key)}"]`;
      return `[data-cart-change]${line}[aria-label="${CSS.escape(label)}"]`;
    }
    return el.id ? `#${CSS.escape(el.id)}` : null;
  }

  function renderCartSections(sections) {
    if (!sections) return;
    for (const [id, html] of Object.entries(sections)) {
      if (!html) continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-root]');
      const current = $(`[data-cart-root][data-cart-section="${id}"]`);
      if (!fresh || !current) continue;
      /* the whole cart is replaced: focus goes back to the same control (a stepper that turned disabled hands it
         to its twin; a removed line hands it to the line now in its place, else the drawer), not to the page */
      const active = document.activeElement;
      const hadFocus = active && active !== document.body && current.contains(active);
      const key = hadFocus ? cartFocusKey(active) : null;
      const line = hadFocus ? active.closest('.pl-cline') : null;
      const lineAt = line ? $$('.pl-cline', current).indexOf(line) : -1;
      current.replaceWith(fresh);
      if (hadFocus) {
        let target = key ? fresh.querySelector(key) : null;
        if (target && target.disabled) target = target.parentElement.querySelector('button:not([disabled])');
        if (!target && lineAt >= 0) {
          const lines = $$('.pl-cline', fresh);
          const near = lines[Math.min(lineAt, lines.length - 1)];
          if (near) target = near.querySelector('.pl-cline__title');
        }
        if (!target) target = fresh.closest('[role="dialog"]') || fresh.querySelector('a[href], button');
        if (target) target.focus({preventScroll: true});
      }
      updateCount(Number(fresh.dataset.count || 0));
    }
  }

  function updateCount(count) {
    for (const badge of $$('[data-cart-count]')) {
      badge.textContent = count;
      badge.hidden = count === 0;
    }
    for (const link of $$('.pl-iconbtn--cart')) {
      link.setAttribute('aria-label', `Cart, ${count} item${count === 1 ? '' : 's'}`);
    }
  }

  /* body: an object (sent as JSON), or a FormData when a line carries a file
     (a logo upload): that is posted as multipart itself, with no
     Content-Type header (the browser sets the boundary), so Shopify stores
     the file and writes its URL into the line property. The cart sections
     ride along either way. */
  async function cartRequest(url, body) {
    const sections = cartSectionIds();
    let init;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      if (sections.length) body.append('sections', sections.join(','));
      body.append('sections_url', window.location.pathname);
      init = {method: 'POST', headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body};
    } else {
      init = {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({
          ...body,
          sections,
          sections_url: window.location.pathname,
        }),
      };
    }
    const response = await fetch(url, init);
    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Something went wrong — please try again.');
    }
    renderCartSections(data.sections);
    document.dispatchEvent(new CustomEvent('pl:cart-updated', {detail: data}));
    return data;
  }
  PL.cartRequest = cartRequest;

  function showCartError(message) {
    for (const slot of $$('[data-cart-error]')) {
      slot.textContent = message;
      slot.hidden = false;
    }
  }

  function setBusy(busy) {
    for (const el of $$('[data-cart-root]')) {
      if (busy) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
    }
  }

  /* add — product forms, quick add, companion cards */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-pl-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const errorSlot = form.querySelector('[data-pl-form-error]') ||
      form.closest('[data-pl-product]')?.querySelector('[data-pl-form-error]');
    if (errorSlot) errorSlot.hidden = true;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    const data = new FormData(form);
    /* a chosen file goes as the form's own multipart data (JSON would turn it into {}) */
    const hasFile = $$('input[type="file"]', form).some((input) => !input.disabled && input.files && input.files.length);
    const item = {id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1)};
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value && !(typeof File !== 'undefined' && value instanceof File)) properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    /* hanging add-ons ticked in the buy box (snippets/pl-pdp-addons): one of each per bag, in the same request; a
       multipart line (a logo file) carries one line, so they follow it */
    const addons = $$('input[data-pl-addon]:checked', form).map((input) => ({id: Number(input.value), quantity: item.quantity}));
    try {
      await cartRequest(routes.cartAdd, hasFile ? data : {items: [item, ...addons]});
      if (hasFile && addons.length) await cartRequest(routes.cartAdd, {items: addons});
      openDrawer('cart');
    } catch (error) {
      if (errorSlot) {
        errorSlot.textContent = error.message;
        errorSlot.hidden = false;
      } else {
        showCartError(error.message);
        openDrawer('cart');
      }
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  });

  /* quantity steppers and remove */
  document.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-change]');
    if (!control) return;
    event.preventDefault();
    setBusy(true);
    try {
      await cartRequest(routes.cartChange, {
        id: control.dataset.key,
        quantity: Number(control.dataset.qty),
      });
    } catch (error) {
      showCartError(error.message);
    } finally {
      setBusy(false);
    }
  });

  /* discount codes — /cart/update.js takes the full comma-separated set */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-cart-discount]');
    if (!form) return;
    event.preventDefault();
    const code = form.elements.discount.value.trim();
    if (!code) return;
    const codes = (form.dataset.codes || '').split(',').filter(Boolean);
    if (!codes.includes(code)) codes.push(code);
    setBusy(true);
    try {
      await cartRequest(routes.cartUpdate, {discount: codes.join(',')});
    } catch (error) {
      showCartError(error.message);
    } finally {
      setBusy(false);
    }
  });

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-discount-remove]');
    if (!remove) return;
    event.preventDefault();
    const form = remove.closest('.pl-csum')?.querySelector('form[data-cart-discount]');
    const codes = (form?.dataset.codes || '').split(',').filter((code) => code && code !== remove.dataset.discountRemove);
    setBusy(true);
    try {
      await cartRequest(routes.cartUpdate, {discount: codes.join(',')});
    } catch (error) {
      showCartError(error.message);
    } finally {
      setBusy(false);
    }
  });

  /* ------------------------------------------------------------------ *
   * Predictive search
   * ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-pl-psearch-input]', this);
      this.results = $('[data-pl-psearch-results]', this);
      this.shortcuts = $('[data-pl-psearch-shortcuts]', this);
      this.status = $('[data-pl-psearch-status]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 170);
      });
      this.addEventListener('keydown', (event) => this.onKey(event));
    }

    /* ArrowDown / ArrowUp walk the results (or the popular searches) and
       back up to the field. */
    onKey(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const visible = (a) => a.offsetParent !== null;
      let links = $$('a', this.results).filter(visible);
      if (!links.length && this.shortcuts && !this.shortcuts.hidden) links = $$('.pl-psearch__popular a', this.shortcuts).filter(visible);
      if (!links.length) return;
      event.preventDefault();
      const index = links.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') (links[index + 1] || links[0]).focus();
      else if (index <= 0) this.input.focus();
      else links[index - 1].focus();
    }

    announce(term, content) {
      if (!this.status) return;
      if (!term || !content) {
        this.status.textContent = '';
        return;
      }
      if (content.dataset.empty === 'true') {
        this.status.textContent = `No results for ${term}`;
        return;
      }
      const products = Number(content.dataset.countProducts || 0);
      const categories = Number(content.dataset.countCollections || 0);
      const parts = [];
      if (products) parts.push(`${products} product${products === 1 ? '' : 's'}`);
      if (categories) parts.push(`${categories} categor${categories === 1 ? 'y' : 'ies'}`);
      this.status.textContent = parts.length ? `${parts.join(', ')} for ${term}` : `Results for ${term}`;
    }

    async search() {
      const term = this.input.value.trim();
      if (this.controller) this.controller.abort();
      if (!term) {
        this.results.innerHTML = '';
        if (this.shortcuts) this.shortcuts.hidden = false;
        this.announce('', null);
        return;
      }
      this.controller = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const response = await fetch(`${routes.predictiveSearch}?${params}`, {signal: this.controller.signal});
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-pl-psearch-content]');
        this.results.innerHTML = content ? content.innerHTML : '';
        if (this.shortcuts) this.shortcuts.hidden = Boolean(content) && content.dataset.empty !== 'true';
        this.announce(term, content);
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('pl-psearch')) customElements.define('pl-psearch', PredictiveSearch);

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-pl-psearch-results] a')) closeDrawers(false);
  });

  /* ------------------------------------------------------------------ *
   * Cards — second image on first hover only
   * ------------------------------------------------------------------ */
  if (matchMedia('(hover: hover)').matches) {
    document.addEventListener(
      'pointerover',
      (event) => {
        const card = event.target.closest && event.target.closest('.pl-card');
        if (!card) return;
        const image = card.querySelector('img[data-pl-hover-src]');
        if (!image) return;
        image.srcset = image.dataset.plHoverSrcset || '';
        image.src = image.dataset.plHoverSrc;
        image.removeAttribute('data-pl-hover-src');
      },
      {passive: true},
    );
  }

  /* Tabbing along a product rail brings the focused card fully into view
     (the rail scrolls sideways; its scrollbar is hidden). */
  document.addEventListener('focusin', (event) => {
    const item = event.target.closest && event.target.closest('.pl-rail > *');
    if (item) item.scrollIntoView({block: 'nearest', inline: 'nearest'});
  });

  /* Colour dots on cards (snippets/pl-card): pointing at a dot (mouse or pen) shows that colourway's photo on the
     card once it has loaded, so a photo the store has just renamed (its old name 404s until pl-group is regenerated)
     never shows as a broken image; leaving the row of dots puts the card's own photo back. A tap is a plain link. */
  function cardPhoto(dot) {
    const card = dot.closest('.pl-card');
    return card ? card.querySelector('.pl-card__media img:not(.pl-card__render)') : null;
  }
  let dotOver = null;
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'touch') return;
    const dot = event.target.closest && event.target.closest('.pl-card__dot[data-img]');
    const img = dot && cardPhoto(dot);
    if (!img || dotOver === dot) return;
    dotOver = dot;
    const next = new Image();
    next.onload = () => {
      if (dotOver !== dot) return;
      if (!('plSrc' in img.dataset)) {
        img.dataset.plSrc = img.getAttribute('src') || '';
        img.dataset.plSrcset = img.getAttribute('srcset') || '';
      }
      img.removeAttribute('srcset');
      img.src = next.src;
    };
    next.src = dot.dataset.img;
  });
  document.addEventListener('pointerout', (event) => {
    const row = event.target.closest && event.target.closest('.pl-card__dots');
    if (!row || row.contains(event.relatedTarget)) return;
    dotOver = null;
    const img = cardPhoto(row);
    if (!img || !('plSrc' in img.dataset)) return;
    if (img.dataset.plSrcset) img.setAttribute('srcset', img.dataset.plSrcset);
    img.src = img.dataset.plSrc;
    delete img.dataset.plSrc;
    delete img.dataset.plSrcset;
  });

  /* Terms and shipping agreement before checkout (owner 2026-10-13; snippets/pl-cart-summary). The box is required;
     with JavaScript the cart shows its own message instead of the browser's, and the tick survives the cart sections
     re-rendering after a quantity change (sessionStorage, this tab only). */
  const AGREE = 'pl-agree';
  const agreed = () => {
    try {
      return sessionStorage.getItem(AGREE) === '1';
    } catch (error) {
      return false;
    }
  };
  function syncAgree() {
    for (const box of $$('[data-pl-agree]')) {
      box.checked = agreed();
      if (box.form) box.form.noValidate = true;
    }
  }
  document.addEventListener('change', (event) => {
    const box = event.target.closest && event.target.closest('[data-pl-agree]');
    if (!box) return;
    try {
      sessionStorage.setItem(AGREE, box.checked ? '1' : '0');
    } catch (error) {
      /* private mode: the tick still counts for this submit */
    }
    for (const other of $$('[data-pl-agree]')) other.checked = box.checked;
    for (const msg of $$('[data-pl-agree-error]')) msg.hidden = true;
  });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest && event.target.closest('.pl-csum__go');
    if (!form) return;
    const box = form.querySelector('[data-pl-agree]');
    if (box && !box.checked) {
      event.preventDefault();
      const msg = form.querySelector('[data-pl-agree-error]');
      if (msg) msg.hidden = false;
      box.closest('.pl-csum__agree').classList.add('is-missing');
      box.focus();
    }
  }, true);
  document.addEventListener('pl:cart-updated', syncAgree);
  syncAgree();

})();
